import React, { FC, useEffect, memo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import ReactGA from 'react-ga4';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from '@mui/material';
import { useSelector } from 'react-redux';
import Navigationbar from './components/Navigation/NavigationBar';
// import Home from './components/Home/HomePage';
import Modals from './components/Modals/Modals';
// import Courses from './components/Courses/Courses';
import CoursesLayout from './components/Courses/CoursesLayout';
// import CourseSingle from './components/Courses/CourseSingle';
import UserLayer from './components/Auth/UserLayer';
import TasksLayout from './components/Task/TasksLayout';
import ProtectedRoute from './components/Smart/ProtectedRoute/ProtectedRoute';
import UserActivation from './components/Auth/UserActivation';
import PATHS from './lib/consts/paths';
import RecoveryPasswordLayout from './components/Auth/RecoveryPassword';
import ScrollToTopAfterPathChange from './components/Smart/ScrollToTopAfterPathChange/ScrollToTopAfterPathChange';
import UserProfileLayout from './components/UserProfile/UserProfileLayout';
import SubscriptionPlanPage from './components/SubscriptionPlans/SubscriptionPlanPage';
import ProductsLayout from './components/Products/ProductsLayout';
import ProductOrder from './components/Products/ProductOrder';
// import ServicePaymentPage from './components/Payment/PaymentPage';
import ProductDetails from './components/Products/ProductDetails';
import MetadataLayer from './components/Smart/MetadataLayer/MetadataLayer';
import ContactUsPage from './components/ContactUs/ContactUsPage';
import CookiesBar from './components/Cookies/CookiesBar';
import CodeEditorPage from './components/CodeEditorPage/CodeEditorPage';
import CodeEditorPageSaved from './components/CodeEditorPage/CodeEditorPageSaved';
import theme, { darkTheme } from './styles/theme';
import { RootState } from './redux/store';
import LegalDocPage from './components/Cookies/CookiesPage';
import { LegalDocs } from './lib/consts';
import TrainingApps from './components/Landing/TrainingApps';
import SubscriptionCancelOrder from './components/SubscriptionPlans/SubscriptionCancel';
import './App.css';
import SubscriptionPayment from './components/Payment/SubscriptionPayment';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			refetchOnReconnect: false,
			retry: false,
		},
	},
});

const GoogleAnalyticsInit = memo(() => {
	ReactGA.initialize(process.env.REACT_APP_GA || 'G-CLDVGC0H9T');
	// eslint-disable-next-line react/jsx-no-useless-fragment
	return <></>;
});

const App: FC = () => {
	const { isDarkTheme } = useSelector((state: RootState) => state.ui.theme);

	useEffect(() => {
		document.body.style.backgroundColor = isDarkTheme ? '#1e1e1e' : '#ffffff';
	}, [isDarkTheme]);

	return (
		<div className="App">
			<ThemeProvider theme={isDarkTheme ? darkTheme : theme}>
				<GoogleOAuthProvider clientId="53114848139-uguli1s40ac9g8e05ii3i21ifet8vias.apps.googleusercontent.com">
					<QueryClientProvider client={queryClient}>
						<MetadataLayer />
						<BrowserRouter>
							{process.env.REACT_APP_ENV !== 'local' && <GoogleAnalyticsInit />}
							<UserLayer />
							<ScrollToTopAfterPathChange />
							<Navigationbar />
							<Modals />
							<CookiesBar version={2} />
							<div className="App__content">
								<Routes>
									<Route path={PATHS.homePage} element={<TrainingApps />} />
									<Route path={PATHS.trainingApps} element={<TrainingApps />} />
									<Route path={PATHS.userActivation} element={<UserActivation />} />
									<Route
										path={PATHS.userPasswordRecovery}
										element={<RecoveryPasswordLayout />}
									/>
									<Route
										path={PATHS.courses}
										element={
											<ProtectedRoute
												redirectPath={PATHS.homePage}
												element={<CoursesLayout />}
											/>
										}
									/>
									{/* <Route path="/courses/:courseSlug/" element={<CourseSingle />} /> */}
									<Route
										path={PATHS.tasks}
										element={
											<ProtectedRoute
												redirectPath={PATHS.homePage}
												element={<TasksLayout />}
											/>
										}
									/>
									<Route path={PATHS.plans} element={<SubscriptionPlanPage />} />
									<Route
										path={`${PATHS.userProfile.root}/:page`}
										element={
											<ProtectedRoute
												redirectPath={PATHS.homePage}
												element={<UserProfileLayout />}
											/>
										}
									/>
									<Route
										path={`${PATHS.subscription.cancelOrder}/:subscriptionId/:orderId`}
										element={
											<ProtectedRoute
												redirectPath={PATHS.homePage}
												element={<SubscriptionCancelOrder />}
											/>
										}
									/>
									<Route path={PATHS.products} element={<ProductsLayout />} />
									<Route
										path={`${PATHS.orders}/:slug`}
										element={
											<ProtectedRoute
												redirectPath={PATHS.homePage}
												element={<ProductOrder />}
											/>
										}
									/>
									<Route path={`${PATHS.products}/:slug`} element={<ProductDetails />} />
									{/* <Route
										path={`${PATHS.payment.success}/:orderNumber`}
										element={
											<ProtectedRoute
												redirectPath={PATHS.homePage}
												element={<ServicePaymentPage />}
											/>
										}
									/> */}
									<Route
										path={`${PATHS.payment.subscriptionPayment}/:orderNumber`}
										element={
											<ProtectedRoute
												redirectPath={PATHS.homePage}
												element={<SubscriptionPayment />}
												disableRedirect
											/>
										}
									/>
									<Route
										path={PATHS.cookiesInfo}
										element={<LegalDocPage identity={LegalDocs.COOKIES_POLICY} />}
									/>
									<Route
										path={PATHS.privacyPolicy}
										element={<LegalDocPage identity={LegalDocs.PRIVACY_POLICY} />}
									/>
									<Route
										path={PATHS.termsAndConditions}
										element={<LegalDocPage identity={LegalDocs.TERMS_AND_CONDITIONS} />}
									/>
									<Route path={PATHS.contactUs} element={<ContactUsPage />} />
									<Route path={PATHS.codeEditorPage} element={<CodeEditorPage />} />
									<Route
										path={PATHS.codeEditorPageSaved}
										element={<CodeEditorPageSaved />}
									/>
									<Route path="*" element={<Navigate to={PATHS.homePage} />} />
								</Routes>
							</div>
						</BrowserRouter>
						<ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
					</QueryClientProvider>
				</GoogleOAuthProvider>
			</ThemeProvider>
		</div>
	);
};

export default App;
